// CRM 2.0 — AI Copilot presentation constants + honest formatting helpers.

import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { TriageBand } from './types';

/** Human labels for the three priority bands. */
export const BAND_LABELS: Record<TriageBand, string> = {
  high: 'High priority',
  medium: 'Medium priority',
  low: 'Low priority',
};

/**
 * Badge tone per band. Higher scores read as stronger/hotter leads: high →
 * success, medium → warning, low → neutral. These are quality tiers over the
 * underlying 0–100 score (Req 9.3), never a fabricated signal.
 */
export const BAND_TONE: Record<TriageBand, BadgeTone> = {
  high: 'success',
  medium: 'warning',
  low: 'neutral',
};

/** Design-fixed band thresholds, mirrored from the server for display labels. */
export const AI_SUGGESTION_LABEL = 'AI_Suggestion';

export const DEPARTMENT_PERSONA: Record<string, { label: string, triageEntity: string, coachEntity: string }> = {
  sales: { label: 'Sales', triageEntity: 'Lead', coachEntity: 'Deal' },
  it: { label: 'IT', triageEntity: 'Project', coachEntity: 'Task' },
};

export const BAND_RANGE_LABEL: Record<TriageBand, string> = {
  high: 'score ≥ 70',
  medium: '40 ≤ score < 70',
  low: 'score < 40',
};

export function bandLabel(band: TriageBand): string {
  return BAND_LABELS[band] ?? band;
}

/** Ordered bands, highest priority first. */
export const BAND_ORDER: TriageBand[] = ['high', 'medium', 'low'];

/** Tone for a factor's signed impact on the win-probability score. */
export function impactTone(impact: number): BadgeTone {
  if (impact > 0) return 'success';
  if (impact < 0) return 'danger';
  return 'neutral';
}

/** Render a signed impact as `+10` / `-15` / `0`. */
export function formatImpact(impact: number): string {
  const n = Number(impact) || 0;
  return n > 0 ? `+${n}` : String(n);
}

/**
 * Format a single-currency money value. Uses `Intl.NumberFormat`; falls back to
 * a code-prefixed number for any currency `Intl` cannot resolve. Currency comes
 * straight from the Deal record and is never combined with another (Req 10.2).
 */
export function formatCurrency(total: number, currency: string | null): string {
  const code = (currency || 'INR').toUpperCase();
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(total || 0);
  } catch {
    return `${code} ${Number(total || 0).toLocaleString('en-IN')}`;
  }
}

/** Plain integer formatting for counts. */
export function formatCount(n: number): string {
  return Number(n || 0).toLocaleString('en-IN');
}

/** Format an ISO-ish date string to a short local date; passthrough on failure. */
export function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}
